import type { Client, Reminder, Service, ServiceType } from '@/core/domain';
import type { ServiceRepository } from '../repositories/service-repository';
import type { CreateClientUseCase } from '@/features/app/clients/domain/usecases/create-client';
import type { CreateReminderUseCase } from '@/features/app/reminders/domain/usecases/create-reminder';

/** Dados do cliente quando for um cliente novo (sem client_id). */
export interface NewClientInput {
  name: string;
  phone: string;
  address?: string | null;
  reference?: string | null;
  notes?: string | null;
}

/** Entrada do use case: informações do serviço e do cliente (existente ou novo). userId é passado separadamente ao execute(). */
export interface CreateServiceInput {
  /** Cliente existente: informar o id. */
  clientId?: string;
  /** Cliente novo: informar os dados. Se clientId estiver preenchido, é ignorado. */
  newClient?: NewClientInput;
  type: ServiceType;
  /** Quando type === 'outro', descrição livre do serviço. */
  type_other_description?: string | null;
  service_date: string;
  next_maintenance_months: number;
  next_maintenance_date: string;
  equipment_reference?: string | null;
  value?: number | null;
  notes?: string | null;
}

export class CreateServiceUseCase {
  constructor(
    private readonly repository: ServiceRepository,
    private readonly createClientUseCase: CreateClientUseCase,
    private readonly createReminderUseCase: CreateReminderUseCase
  ) {}

  async execute(userId: string, input: CreateServiceInput): Promise<Service> {
    let clientId: string;

    if (input.clientId) {
      clientId = input.clientId;
    } else if (input.newClient) {
      const now = new Date().toISOString();
      const client: Client = {
        id: 'pending',
        user_id: userId,
        name: input.newClient.name.trim(),
        phone: input.newClient.phone.trim(),
        address: input.newClient.address?.trim() ?? null,
        reference: input.newClient.reference?.trim() ?? null,
        notes: input.newClient.notes?.trim() ?? null,
        created_at: now,
        updated_at: now,
      };
      const createdClient = await this.createClientUseCase.execute(userId, client);
      clientId = createdClient.id;
    } else {
      throw new Error('É necessário informar clientId (cliente existente) ou newClient (cliente novo).');
    }

    const now = new Date().toISOString();
    const service: Service = {
      id: '',
      user_id: userId,
      client_id: clientId,
      type: input.type,
      type_other_description: input.type_other_description ?? null,
      service_date: input.service_date,
      next_maintenance_months: input.next_maintenance_months,
      next_maintenance_date: input.next_maintenance_date,
      equipment_reference: input.equipment_reference ?? null,
      value: input.value ?? null,
      notes: input.notes ?? null,
      created_at: now,
      updated_at: now,
    };

    const createdService = await this.repository.create(userId, service);

    const reminder: Reminder = {
      id: createdService.id,
      user_id: userId,
      client_id: clientId,
      service_id: createdService.id,
      expected_date: createdService.next_maintenance_date,
      equipment_reference: createdService.equipment_reference,
      status: 'scheduled',
      sent_at: [],
      send_failures: [],
      contacted_at: null,
      scheduled_date: null,
      value_charged: null,
      create_reminder_for_date: false,
      created_at: now,
      updated_at: now,
    };
    await this.createReminderUseCase.execute(reminder);

    return createdService;
  }
}
